import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Cpu,
  Database,
  Loader2,
  RefreshCw,
  Server,
} from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdminLayout } from "@/components/admin/AdminLayout";
import {
  AdminEmptyState,
  AdminLoading,
  AdminMetric,
  AdminPage,
  AdminPanel,
  AdminStatusPill,
} from "@/components/admin/AdminPrimitives";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

type UnitEconomicsSummary = {
  period: {
    start: string;
    end: string;
    days: number;
    targetMargin: number;
  };
  revenue: {
    activeMrrUsd: number;
    targetGrossMarginPct: number;
    grossMarginPct: number | null;
    allowedCogsUsd: number;
    measuredCogsUsd: number;
    requiredMrrForTargetMarginUsd: number;
    mrrGapUsd: number;
  };
  browserbase: {
    sessions: number;
    hours: number;
    pagesLoaded: number;
    reviewsFound: number;
    proxyGb: number;
    usageListRateUsd: number;
    estimatedInvoiceUsd: number;
    includedHours: number;
    includedProxyGb: number;
    overageHours: number;
    overageProxyGb: number;
    baseUsd: number;
    lastMetricAt: string | null;
  };
  llm: {
    calls: number;
    inputTokens: number;
    outputTokens: number;
    cacheCreationInputTokens: number;
    cacheReadInputTokens: number;
    costUsd: number;
    lastMetricAt: string | null;
  };
  aws: {
    costUsd: number;
    lastRefreshedAt: string | null;
    services: Array<{ service: string; costUsd: number }>;
  };
  plans: Array<{
    tier: string;
    activeCustomerCount: number;
    trialCustomerCount: number;
    monitoredAsins: number;
    activeMrrUsd: number;
    allowedCostUsd: number;
    allowedCostPerAsinUsd: number | null;
    monthlyPriceUsd: number;
  }>;
  products: {
    activeMonitoredAsins: number;
    trialMonitoredAsins: number;
    costPerActiveMonitoredAsinUsd: number | null;
    allowedCostPerActiveMonitoredAsinUsd: number | null;
  };
  assumptions: string[];
};

function formatCurrency(value: number | null | undefined, fractionDigits = 2) {
  if (value == null || !Number.isFinite(value)) return "--";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

function formatNumber(value: number | null | undefined, fractionDigits = 0) {
  if (value == null || !Number.isFinite(value)) return "--";
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

function formatPercent(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return "--";
  return `${formatNumber(value, 1)}%`;
}

function formatDateTime(value: string | null) {
  if (!value) return "Never";
  return new Date(value).toLocaleString();
}

function titleCase(value: string) {
  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1))
    .join(" ");
}

function marginTone(value: number | null) {
  if (value == null) return "neutral" as const;
  if (value >= 75) return "success" as const;
  if (value >= 50) return "warning" as const;
  return "danger" as const;
}

function AdminUnitEconomicsInner() {
  const [summary, setSummary] = useState<UnitEconomicsSummary | null>(null);
  const [days, setDays] = useState(30);
  const [targetMargin, setTargetMargin] = useState(75);
  const [loading, setLoading] = useState(true);
  const [refreshingAws, setRefreshingAws] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams({
      days: String(days),
      targetMargin: String(targetMargin),
    });
    return params.toString();
  }, [days, targetMargin]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api<UnitEconomicsSummary>(`/admin/unit-economics?${query}`).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (!res.ok || !res.data) {
        setSummary(null);
        setError(res.error ?? "Could not load unit economics.");
        return;
      }
      setSummary(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, [query, reloadKey]);

  async function refreshAwsSpend() {
    setRefreshingAws(true);
    setError(null);
    const res = await api("/admin/unit-economics/refresh-aws", {
      method: "POST",
      body: JSON.stringify({ days }),
    });
    setRefreshingAws(false);
    if (!res.ok) {
      setError(res.error ?? "Could not refresh AWS spend.");
      return;
    }
    setReloadKey((key) => key + 1);
  }

  return (
    <AdminPage
      eyebrow="Finance"
      title="Unit economics"
      description="Monitor Browserbase, LLM, and AWS COGS against target SaaS margin before changing ASIN allowances."
      actions={
        <>
          <select
            className="h-9 rounded-lg border border-border bg-background px-3 text-sm"
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
          <label className="flex h-9 items-center gap-2 rounded-lg border border-border bg-background px-3 text-sm">
            <span className="text-muted-foreground">Margin</span>
            <input
              className="w-12 bg-transparent text-right outline-none"
              min={1}
              max={95}
              type="number"
              value={targetMargin}
              onChange={(event) => setTargetMargin(Number(event.target.value))}
            />
            <span className="text-muted-foreground">%</span>
          </label>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setReloadKey((key) => key + 1)}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </Button>
        </>
      }
    >
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      )}

      {loading && !summary && <AdminLoading label="Loading unit economics" />}

      {summary && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <AdminMetric
              label="Active MRR"
              value={formatCurrency(summary.revenue.activeMrrUsd, 0)}
              detail={`${formatNumber(summary.products.activeMonitoredAsins)} active monitored ASINs`}
            />
            <AdminMetric
              label="Measured COGS"
              value={formatCurrency(summary.revenue.measuredCogsUsd, 0)}
              detail={`${formatNumber(summary.period.days)} day cost window`}
            />
            <AdminMetric
              label="Gross Margin"
              value={formatPercent(summary.revenue.grossMarginPct)}
              tone={marginTone(summary.revenue.grossMarginPct)}
              detail={`Target ${formatPercent(summary.revenue.targetGrossMarginPct)}`}
            />
            <AdminMetric
              label="MRR For Target"
              value={formatCurrency(summary.revenue.requiredMrrForTargetMarginUsd, 0)}
              detail={
                summary.revenue.mrrGapUsd > 0
                  ? `${formatCurrency(summary.revenue.mrrGapUsd, 0)} gap`
                  : "Target covered"
              }
            />
          </div>

          <div className="grid gap-4 xl:grid-cols-3">
            <AdminPanel
              title="COGS Breakdown"
              description="Costs used in the margin calculation."
            >
              <div className="divide-y divide-border text-sm">
                <CostRow
                  icon={<Server className="h-4 w-4" />}
                  label="Browserbase"
                  value={summary.browserbase.estimatedInvoiceUsd}
                  detail={`${formatNumber(summary.browserbase.hours, 2)} hrs, ${formatNumber(summary.browserbase.proxyGb, 3)} GB proxy`}
                />
                <CostRow
                  icon={<Cpu className="h-4 w-4" />}
                  label="LLM tokens"
                  value={summary.llm.costUsd}
                  detail={`${formatNumber(summary.llm.calls)} calls`}
                />
                <CostRow
                  icon={<Database className="h-4 w-4" />}
                  label="AWS"
                  value={summary.aws.costUsd}
                  detail={`Last refresh ${formatDateTime(summary.aws.lastRefreshedAt)}`}
                />
              </div>
            </AdminPanel>

            <AdminPanel
              title="Browserbase Usage"
              description="Measured session inputs for ASIN limit decisions."
            >
              <div className="grid grid-cols-2 gap-px bg-border text-sm">
                <StatCell label="Sessions" value={formatNumber(summary.browserbase.sessions)} />
                <StatCell label="Pages loaded" value={formatNumber(summary.browserbase.pagesLoaded)} />
                <StatCell label="Reviews found" value={formatNumber(summary.browserbase.reviewsFound)} />
                <StatCell label="Proxy GB" value={formatNumber(summary.browserbase.proxyGb, 3)} />
                <StatCell label="Included hours" value={formatNumber(summary.browserbase.includedHours, 1)} />
                <StatCell label="Overage hours" value={formatNumber(summary.browserbase.overageHours, 2)} />
              </div>
              <div className="border-t border-border px-4 py-3 text-xs text-muted-foreground">
                Base applied: {formatCurrency(summary.browserbase.baseUsd)}. Last metric:{" "}
                {formatDateTime(summary.browserbase.lastMetricAt)}.
              </div>
            </AdminPanel>

            <AdminPanel title="LLM Usage" description="Classifier token spend.">
              <div className="grid grid-cols-2 gap-px bg-border text-sm">
                <StatCell label="Input tokens" value={formatNumber(summary.llm.inputTokens)} />
                <StatCell label="Output tokens" value={formatNumber(summary.llm.outputTokens)} />
                <StatCell
                  label="Cache writes"
                  value={formatNumber(summary.llm.cacheCreationInputTokens)}
                />
                <StatCell
                  label="Cache reads"
                  value={formatNumber(summary.llm.cacheReadInputTokens)}
                />
              </div>
              <div className="border-t border-border px-4 py-3 text-xs text-muted-foreground">
                Last metric: {formatDateTime(summary.llm.lastMetricAt)}.
              </div>
            </AdminPanel>
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.8fr)]">
            <AdminPanel title="Plan Guardrails" description="Allowed COGS at the selected margin.">
              {summary.plans.length === 0 ? (
                <AdminEmptyState
                  title="No active or trial subscriptions"
                  description="Plan guardrails will appear once subscriptions exist."
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] text-left text-sm">
                    <thead className="border-b border-border bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3 font-medium">Plan</th>
                        <th className="px-4 py-3 font-medium">Customers</th>
                        <th className="px-4 py-3 font-medium">ASINs</th>
                        <th className="px-4 py-3 font-medium">MRR</th>
                        <th className="px-4 py-3 font-medium">COGS Budget</th>
                        <th className="px-4 py-3 font-medium">Budget / ASIN</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {summary.plans.map((plan) => (
                        <tr key={plan.tier}>
                          <td className="px-4 py-3">
                            <div className="font-medium">{titleCase(plan.tier)}</div>
                            <div className="text-xs text-muted-foreground">
                              {plan.monthlyPriceUsd > 0
                                ? `${formatCurrency(plan.monthlyPriceUsd, 0)}/mo`
                                : "Custom"}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>{formatNumber(plan.activeCustomerCount)} active</div>
                            <div className="text-xs text-muted-foreground">
                              {formatNumber(plan.trialCustomerCount)} trial
                            </div>
                          </td>
                          <td className="px-4 py-3">{formatNumber(plan.monitoredAsins)}</td>
                          <td className="px-4 py-3">{formatCurrency(plan.activeMrrUsd, 0)}</td>
                          <td className="px-4 py-3">{formatCurrency(plan.allowedCostUsd, 0)}</td>
                          <td className="px-4 py-3">
                            {formatCurrency(plan.allowedCostPerAsinUsd)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </AdminPanel>

            <AdminPanel
              title="AWS Spend"
              description="Cost Explorer UnblendedCost by service."
              actions={
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={refreshAwsSpend}
                  disabled={refreshingAws}
                >
                  {refreshingAws ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Refresh AWS
                </Button>
              }
            >
              {summary.aws.services.length === 0 ? (
                <AdminEmptyState
                  title="No AWS spend loaded"
                  description="Refresh AWS spend to pull the latest Cost Explorer data."
                />
              ) : (
                <div className="divide-y divide-border">
                  {summary.aws.services.map((service) => (
                    <div
                      key={service.service}
                      className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
                    >
                      <div className="min-w-0 truncate">{service.service}</div>
                      <div className="shrink-0 font-medium">
                        {formatCurrency(service.costUsd)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </AdminPanel>
          </div>

          <AdminPanel title="Margin Inputs">
            <div className="grid gap-3 px-4 py-4 text-sm sm:grid-cols-2 xl:grid-cols-4">
              <InputPill
                label="Allowed active ASIN COGS"
                value={formatCurrency(
                  summary.products.allowedCostPerActiveMonitoredAsinUsd
                )}
              />
              <InputPill
                label="Measured active ASIN COGS"
                value={formatCurrency(summary.products.costPerActiveMonitoredAsinUsd)}
              />
              <InputPill
                label="Trial monitored ASINs"
                value={formatNumber(summary.products.trialMonitoredAsins)}
              />
              <InputPill
                label="COGS allowance"
                value={formatCurrency(summary.revenue.allowedCogsUsd, 0)}
              />
            </div>
            <div className="border-t border-border px-4 py-3">
              <div className="flex flex-wrap gap-2">
                {summary.assumptions.map((assumption) => (
                  <AdminStatusPill key={assumption}>{assumption}</AdminStatusPill>
                ))}
              </div>
            </div>
          </AdminPanel>
        </>
      )}
    </AdminPage>
  );
}

function CostRow({
  icon,
  label,
  value,
  detail,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  detail: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <div className="flex min-w-0 items-center gap-2">
        <span className="text-muted-foreground">{icon}</span>
        <div className="min-w-0">
          <div className="font-medium">{label}</div>
          <div className="truncate text-xs text-muted-foreground">{detail}</div>
        </div>
      </div>
      <div className="shrink-0 font-semibold">{formatCurrency(value)}</div>
    </div>
  );
}

function StatCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-card px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}

function InputPill({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border px-3 py-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 font-semibold">{value}</div>
    </div>
  );
}

export default function AdminUnitEconomics() {
  return (
    <AdminGate>
      <AdminLayout>
        <AdminUnitEconomicsInner />
      </AdminLayout>
    </AdminGate>
  );
}
